// src/pages/InvoiceDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invoicesAPI } from '../services/api';

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReminderOptions, setShowReminderOptions] = useState(false);
  // Neue Zustände für das E-Mail-Modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    recipient_email: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    const fetchInvoiceData = async () => {
      setLoading(true);
      try {
        const response = await invoicesAPI.getById(id);
        setInvoice(response.data.invoice);
        
        // Wenn die Rechnung einen Kundenemailkontakt hat, diesen vorausfüllen
        if (response.data.invoice.customer_email) {
          setEmailData(prevData => ({
            ...prevData,
            recipient_email: response.data.invoice.customer_email,
            subject: `Rechnung #${response.data.invoice.id} - B-Storen-Service`,
            message: `Sehr geehrte Damen und Herren,

anbei erhalten Sie die Rechnung #${response.data.invoice.id}.

Zahlbar innert 20 Tagen ohne Abzug.

Mit freundlichen Grüssen,
B-Storen-Service`
          }));
        }
        
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Rechnung:', err);
        setError('Die Rechnung konnte nicht geladen werden: ' + 
                 (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInvoiceData();
    }
  }, [id]);

  // Neue Funktion zum Verarbeiten von Änderungen in den E-Mail-Formularfeldern
  const handleEmailInputChange = (e) => {
    const { name, value } = e.target;
    setEmailData({
      ...emailData,
      [name]: value
    });
  };

  // Neue Funktion zum Senden der E-Mail
  const sendInvoiceByEmail = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    setEmailError('');
    setEmailSuccess('');
    
    try {
      const response = await invoicesAPI.sendByEmail(id, emailData);
      setEmailSuccess('Rechnung wurde erfolgreich per E-Mail versendet.');
      // Modal nach kurzer Verzögerung schließen, damit der Benutzer die Erfolgsmeldung lesen kann
      setTimeout(() => {
        setShowEmailModal(false);
        // Erfolgsmeldung nach dem Schließen zurücksetzen
        setEmailSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Fehler beim Versenden der Rechnung per E-Mail:', err);
      setEmailError('Fehler beim Versenden der E-Mail: ' + 
                  (err.response?.data?.error || err.message));
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadPDF = async () => {
    try {
      console.log('PDF-Download für Rechnung:', id);
      
      // Statt Blob-Ansatz direkt die API-URL öffnen
      const token = localStorage.getItem('token');
      const timestamp = new Date().getTime(); // Zeitstempel hinzufügen, um den Cache zu umgehen
      const apiUrl = `${process.env.REACT_APP_API_URL || '/api'}/invoices/${id}/pdf`;
      
      // URL mit Token als Query-Parameter für die Authentifizierung und Cache-Busting
      const pdfUrl = `${apiUrl}?token=${token}&t=${timestamp}`;
      
      // PDF in einem neuen Tab öffnen
      window.open(pdfUrl, '_blank');
    } catch (err) {
      console.error('Fehler beim Öffnen der Rechnung:', err);
      setError('Fehler beim Öffnen der Rechnung');
    }
  };

  const markAsPaid = async () => {
    if (!window.confirm('Möchten Sie diese Rechnung als bezahlt markieren?')) return;
    
    try {
      const now = new Date();
      const mysqlDate = now.toISOString().slice(0, 19).replace('T', ' ');
      
      await invoicesAPI.markAsPaid(id, { paid_at: mysqlDate });
      
      // Rechnung neu laden, um den aktualisierten Status anzuzeigen
      const response = await invoicesAPI.getById(id);
      setInvoice(response.data.invoice);
      setError('');
    } catch (err) {
      console.error('Fehler beim Markieren der Rechnung als bezahlt:', err);
      setError('Fehler beim Markieren der Rechnung als bezahlt: ' + 
               (err.response?.data?.error || err.message));
    }
  };

  const reopenInvoice = async () => {
    if (!window.confirm('Möchten Sie diese Rechnung wirklich wieder öffnen? Der Bezahlt-Status wird zurückgesetzt.')) return;
    
    try {
      await invoicesAPI.reopenInvoice(id);
      
      // Rechnung neu laden, um den aktualisierten Status anzuzeigen
      const response = await invoicesAPI.getById(id);
      setInvoice(response.data.invoice);
      setError('');
      alert('Rechnung erfolgreich wieder geöffnet');
    } catch (err) {
      console.error('Fehler beim Wieder-Öffnen der Rechnung:', err);
      setError('Fehler beim Wieder-Öffnen der Rechnung: ' + 
               (err.response?.data?.error || err.message));
    }
  };

  // Neue Funktion zum Erstellen einer Mahnung
  const createReminder = async (reminderLevel) => {
    try {
      await invoicesAPI.createReminder(id, { reminder_level: reminderLevel });
      
      // Rechnung neu laden, um die aktualisierte Version anzuzeigen
      const response = await invoicesAPI.getById(id);
      setInvoice(response.data.invoice);
      setError('');
      setShowReminderOptions(false);
      alert(`Mahnung Stufe ${reminderLevel} erfolgreich erstellt`);
    } catch (err) {
      console.error('Fehler beim Erstellen der Mahnung:', err);
      setError('Fehler beim Erstellen der Mahnung: ' + 
               (err.response?.data?.error || err.message));
    }
  };

  // Status in lesbaren Text umwandeln
  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Ausstehend',
      paid: 'Bezahlt',
      overdue: 'Überfällig'
    };
    return statusMap[status] || status;
  };

  // Status-Farbe bestimmen
  const getStatusColor = (status) => {
    const colorMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  // Datum formatieren
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Betrag formatieren
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return 'CHF 0.00';
    return 'CHF ' + parseFloat(amount).toFixed(2);
  };

  // Formatiere Zahlen für Anzeige
  const formatNumber = (number) => {
    if (number === undefined || number === null) return '0.00';
    return parseFloat(number).toFixed(2);
  };

  if (loading) {
    return <div className="text-center py-6 sm:py-10">Lade Rechnungsdaten...</div>;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6">
          <p className="text-sm sm:text-base text-red-700">{error}</p>
        </div>
        <button 
          onClick={() => navigate('/rechnungen')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
        >
          Zurück zur Rechnungsübersicht
        </button>
      </div>
    );
  }

  if (!invoice) {
    return <div className="text-center py-6 sm:py-10">Rechnung nicht gefunden</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
        <h1 className="text-xl sm:text-2xl font-semibold">Rechnung #{invoice.id}</h1>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <button 
            onClick={() => navigate('/rechnungen')}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 w-full sm:w-auto"
          >
            Zurück
          </button>
          <button 
            onClick={downloadPDF}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded w-full sm:w-auto"
          >
            Als PDF
          </button>
          {/* Neuer Button zum Öffnen des E-Mail-Modals */}
          <button 
            onClick={() => setShowEmailModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full sm:w-auto"
          >
            Per E-Mail senden
          </button>
          {(invoice.status === 'pending' || invoice.status === 'overdue') && (
            <>
              <button 
                onClick={markAsPaid}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded w-full sm:w-auto"
              >
                Als bezahlt markieren
              </button>
              <button 
                onClick={() => navigate(`/rechnungen/${invoice.id}/bearbeiten`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full sm:w-auto"
              >
                Bearbeiten
              </button>
            </>
          )}
          {invoice.status === 'paid' && (
            <button 
              onClick={reopenInvoice}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded w-full sm:w-auto"
            >
              Wieder öffnen
            </button>
          )}
          {(invoice.status === 'pending' || invoice.status === 'overdue') && (
            <div className="relative">
              <button 
                onClick={() => setShowReminderOptions(!showReminderOptions)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded w-full sm:w-auto"
              >
                Mahnen
              </button>
              {showReminderOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-10">
                  <ul>
                    <li 
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer" 
                      onClick={() => createReminder(1)}
                    >
                      Mahnung Stufe 1
                    </li>
                    <li 
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer" 
                      onClick={() => createReminder(2)}
                    >
                      Mahnung Stufe 2
                    </li>
                    <li 
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer" 
                      onClick={() => createReminder(3)}
                    >
                      Mahnung Stufe 3
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rechnungsdetails */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between mb-4 sm:mb-6 gap-4">
          <div>
            <h2 className="text-lg sm:text-xl font-medium mb-4">Rechnungsdetails</h2>
            <p><strong>Rechnungsnummer:</strong> {invoice.id}</p>
            <p><strong>Datum:</strong> {formatDate(invoice.created_at)}</p>
            <p><strong>Fälligkeitsdatum:</strong> {invoice.payment_type === 'cash' ? 'Sofort' : formatDate(invoice.due_date)}</p>
            <p>
              <strong>Status:</strong>{' '}
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                {getStatusText(invoice.status)}
              </span>
            </p>
            <p><strong>Zahlungsart:</strong> {invoice.payment_type === 'cash' ? 'Bar' : 'Rechnung'}</p>
            {invoice.status === 'paid' && (
              <p><strong>Bezahlt am:</strong> {formatDate(invoice.paid_at)}</p>
            )}
            {invoice.reminder_level && (
              <p><strong>Mahnungsstufe:</strong> {invoice.reminder_level}</p>
            )}
          </div>
          <div className="mb-4 sm:mb-0">
            <h3 className="font-medium mb-2">Zahlungsempfänger</h3>
            <p>Boris Blachen & Storen GmbH</p>
            <p>Schönenweg 2</p>
            <p>8733 Eschenbach SG</p>
            <p>Tel: 077 493 81 41</p>
            <p>info@b-storenservice.ch</p>
          </div>
          <div>
            <h3 className="font-medium mb-2">Rechnungsempfänger</h3>
            <p>{invoice.customer_name}</p>
            {invoice.customer_additional_address && (
              <p>{invoice.customer_additional_address}</p>
            )}
            <p>{invoice.customer_address}</p>
            <p>{invoice.customer_postal_code} {invoice.customer_city}</p>
            <p>{invoice.customer_email}</p>
          </div>
        </div>

        {/* Projekt- und Rapportdaten */}
        <div className="mb-6">
          <h3 className="font-medium mb-2">Auftrag</h3>
          <p><strong>Auftragsname:</strong> {invoice.project_title}</p>
          <p><strong>Rapport-Nr.:</strong> {invoice.report_id}</p>
          {invoice.notes && (
            <>
              <h3 className="font-medium mt-4 mb-2">Bemerkungen</h3>
              <p>{invoice.notes}</p>
            </>
          )}
        </div>

        {/* Auflistung der Leistungen */}
        <div className="mb-6">
          <h3 className="font-medium mb-4">Leistungen</h3>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Menge</th>
                  <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Einheit</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preis</th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Betrag</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Materialien */}
                {invoice.materials && invoice.materials.map((material, index) => (
                  <tr key={`material-${index}`}>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-normal">
                      <div className="text-xs sm:text-sm text-gray-900">{material.material_name}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{material.quantity}</div>
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{material.unit}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{formatAmount(material.price_per_unit)}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{formatAmount(material.quantity * material.price_per_unit)}</div>
                    </td>
                  </tr>
                ))}
                
                {/* Arbeitsstunden */}
                <tr>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-normal">
                    <div className="text-xs sm:text-sm text-gray-900">Arbeitsstunden</div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <div className="text-xs sm:text-sm text-gray-900">{formatNumber(invoice.labor_hours)}</div>
                  </td>
                  <td className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">h</div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <div className="text-xs sm:text-sm text-gray-900">{formatAmount(invoice.hourly_rate)}</div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <div className="text-xs sm:text-sm text-gray-900">{formatAmount(invoice.labor_cost)}</div>
                  </td>
                </tr>

                {/* Anfahrtspauschale, falls vorhanden */}
                {invoice.travel_allowance > 0 && (
                  <tr>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-normal">
                      <div className="text-xs sm:text-sm text-gray-900">Anfahrtspauschale</div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">1</div>
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">Pauschal</div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{formatAmount(invoice.travel_allowance)}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{formatAmount(invoice.travel_allowance)}</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summen */}
        <div className="flex justify-end">
          <div className="w-full sm:w-64">
            {(() => {
              // Zwischensumme aus den angezeigten Materialien berechnen
              const materialSum = invoice.materials?.reduce((sum, m) => {
                return sum + (parseFloat(m.quantity || 0) * parseFloat(m.price_per_unit || 0));
              }, 0) || 0;
              
              const originalSubtotal = (parseFloat(invoice.labor_cost) || 0) + 
                                     (parseFloat(invoice.travel_allowance) || 0) + 
                                     materialSum;
              
              // Rabatt berechnen
              const discountAmount = invoice.discount && parseFloat(invoice.discount) > 0
                ? originalSubtotal * (parseFloat(invoice.discount) / 100)
                : 0;
              
              const subtotalAfterDiscount = originalSubtotal - discountAmount;
              
              // MwSt berechnen
              const taxAmount = invoice.no_tax ? 0 : subtotalAfterDiscount * 0.077;
              
              // Gesamtbetrag
              const totalAmount = subtotalAfterDiscount + taxAmount;
              
              return (
                <>
                  <div className="flex justify-between py-2">
                    <span className="font-medium">Zwischensumme:</span>
                    <span>{formatAmount(originalSubtotal)}</span>
                  </div>
                  {invoice.discount && parseFloat(invoice.discount) > 0 && (
                    <div className="flex justify-between py-2 text-red-600">
                      <span>Rabatt ({invoice.discount}%):</span>
                      <span>- {formatAmount(discountAmount)}</span>
                    </div>
                  )}
                  {!invoice.no_tax && (
                    <div className="flex justify-between py-2">
                      <span>MwSt. (7.7%):</span>
                      <span>{formatAmount(taxAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 text-lg font-bold border-t border-gray-200 pt-2">
                    <span>Gesamtbetrag:</span>
                    <span>{formatAmount(totalAmount)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Zahlungsinformationen */}
      {invoice.payment_type === 'invoice' && invoice.status !== 'paid' && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <h2 className="text-lg sm:text-xl font-medium mb-4">Zahlungsinformationen</h2>
          <p><strong>Kontoinhaber:</strong> Boris Blachen & Storen GmbH</p>
          <p><strong>Bank:</strong> Sample Bank AG</p>
          <p><strong>IBAN:</strong> CH00 0000 0000 0000 0000 0</p>
          <p><strong>Verwendungszweck:</strong> Rechnungsnummer {invoice.id}</p>
          <p className="mt-4"><strong>Zahlungsfrist:</strong> 20 Tage ({formatDate(invoice.due_date)})</p>
        </div>
      )}

      {/* E-Mail-Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Rechnung per E-Mail versenden</h2>
            
            {emailSuccess && (
              <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-4">
                <p className="text-sm text-green-700">{emailSuccess}</p>
              </div>
            )}
            
            {emailError && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4">
                <p className="text-sm text-red-700">{emailError}</p>
              </div>
            )}
            
            <form onSubmit={sendInvoiceByEmail}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recipient_email">
                  Empfänger E-Mail
                </label>
                <input
                  id="recipient_email"
                  name="recipient_email"
                  type="email"
                  required
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={emailData.recipient_email}
                  onChange={handleEmailInputChange}
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="subject">
                  Betreff
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={emailData.subject}
                  onChange={handleEmailInputChange}
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="message">
                  Nachricht
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={emailData.message}
                  onChange={handleEmailInputChange}
                ></textarea>
              </div>
              
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  onClick={() => setShowEmailModal(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  disabled={sendingEmail}
                >
                  {sendingEmail ? 'Wird gesendet...' : 'Senden'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetailPage;
